import { IonIcon } from '@ionic/react';
import { camera, checkmarkCircle } from 'ionicons/icons';
import type { CandidateProfile, IdentityVerification } from '@/types';
import type { NpsAggregateResult } from '@/types';

interface ProfileHeaderProps {
  profile: CandidateProfile;
  verification?: IdentityVerification | null;
  npsAggregate?: NpsAggregateResult;
  onPhotoClick?: () => void;
}

export function ProfileHeader({ profile, verification, npsAggregate, onPhotoClick }: ProfileHeaderProps) {
  const isVerified = verification?.status === 'Verified';

  return (
    <div style={{
      display: 'flex', flexDirection: 'column', alignItems: 'center',
      padding: '24px 16px', gap: 12,
    }}>
      <div style={{ position: 'relative' }}>
        <div
          style={{
            width: 96, height: 96, borderRadius: '50%',
            background: 'linear-gradient(135deg, var(--bp-blue-500, #2563eb), var(--bp-cyan-400, #22d3ee))',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            fontSize: '2rem', fontWeight: 700, color: '#fff',
            overflow: 'hidden',
          }}
        >
          {profile.profileImageRef ? (
            <img
              src={`/api/v1/files/${profile.profileImageRef.id}`}
              alt="Profile"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : (
            profile.headline?.charAt(0)?.toUpperCase() ?? '?'
          )}
        </div>
        {onPhotoClick && (
          <button
            type="button"
            onClick={onPhotoClick}
            aria-label="Change photo"
            style={{
              position: 'absolute', bottom: 0, right: 0,
              width: 28, height: 28, borderRadius: '50%',
              background: 'var(--bp-blue-500, #2563eb)',
              border: '2px solid var(--bp-surface, #111827)',
              display: 'flex', alignItems: 'center', justifyContent: 'center',
              cursor: 'pointer',
            }}
          >
            <IonIcon icon={camera} style={{ fontSize: '0.8rem', color: '#fff' }} />
          </button>
        )}
      </div>

      <div style={{ textAlign: 'center' }}>
        <h2 style={{ margin: 0, fontSize: '1.25rem', fontWeight: 700 }}>
          {profile.headline || 'Untitled Profile'}
        </h2>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, marginTop: 4 }}>
          <span style={{
            fontSize: '0.7rem', padding: '2px 8px', borderRadius: 12,
            background: 'rgba(255,255,255,0.08)', color: 'var(--bp-text-secondary, #94a3b8)',
          }}>
            {profile.profileType ?? 'Individual'}
          </span>
          {isVerified && (
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 3,
              fontSize: '0.7rem', color: 'var(--bp-success, #22c55e)',
            }}>
              <IonIcon icon={checkmarkCircle} style={{ fontSize: '0.85rem' }} />
              Verified
            </span>
          )}
        </div>
      </div>

      {npsAggregate && npsAggregate.total > 0 && (
        <div style={{
          display: 'flex', gap: 16, fontSize: '0.75rem',
          color: 'var(--bp-text-secondary, #94a3b8)',
        }}>
          <span>NPS <strong style={{ color: 'var(--bp-text, #f1f5f9)' }}>{npsAggregate.score}</strong></span>
          <span>{npsAggregate.total} rating{npsAggregate.total !== 1 ? 's' : ''}</span>
        </div>
      )}
    </div>
  );
}
